<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { ChevronDownIcon } from '@heroicons/vue/24/outline'
import { computed, inject } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { CollapsibleTrigger } from '#ui-kit/ui/collapsible'
import { CARD_COLLAPSIBLE_KEY } from './Card.vue'

const props = defineProps<{
  class?: HTMLAttributes['class']
  sticky?: boolean
}>()

const card = inject(CARD_COLLAPSIBLE_KEY, null)
const collapsible = computed(() => card?.collapsible.value ?? false)
const collapsed = computed(() => collapsible.value && !card?.open.value)

function onClick(event: MouseEvent) {
  if (!card || !collapsible.value) {
    return
  }

  const target = event.target instanceof Element ? event.target : null
  const control = target?.closest('a, button, input, select, textarea, label, [role="button"]')

  if (control && event.currentTarget instanceof Element && event.currentTarget.contains(control)) {
    return
  }

  card.toggle()
}
</script>

<template>
  <div
    data-slot="card-header"
    :class="
      cn(
        'flex min-h-14 items-center justify-between gap-2 border-b border-default px-5',
        collapsed && 'border-b-0',
        collapsible && 'cursor-pointer select-none',
        sticky && 'sticky top-0 z-10 rounded-t-lg bg-inherit',
        props.class,
      )
    "
    @click="onClick"
  >
    <slot />
    <CollapsibleTrigger
      v-if="collapsible"
      class="group/trigger -me-1 flex shrink-0 items-center justify-center rounded-md p-1 text-gray-600 hover:text-primal"
      aria-label="Toggle"
    >
      <ChevronDownIcon class="size-5 transition-transform duration-300 group-data-[state=open]/trigger:rotate-180" />
    </CollapsibleTrigger>
  </div>
</template>
